import { useTranslation } from 'react-i18next';
import {
    useAppSelector,
    useTheme,
    Skeleton,
    Typography,
    Box,
    Icon,
} from '../../../../base';
import { Directions } from '../../../../common/consts/system.consts';
import { reportsSelectors } from '../../../../actions';
import { StyledConversionCard } from '../StageConversionRates/StageConversionRates.styles';
import { getConversionSteps, isJobView } from '../utils/funnelDataUtils';

const DropOffRates = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const funnelData = useAppSelector(reportsSelectors.getFunnelData);
    const isLoading = useAppSelector(reportsSelectors.getIsLoadingFunnel);
    const isRtl = theme.direction === Directions.rtl;

    if (isLoading) {
        return (
            <Skeleton
                variant="rectangular"
                width="100%"
                height={250}
                sx={{ borderRadius: 1 }}
            />
        );
    }

    const conversionSteps = getConversionSteps(funnelData);

    const dropOffItems = conversionSteps.map((step) => ({
        label: `${step.fromLabelKey ? t(step.fromLabelKey) : step.fromLabel} ${isRtl ? '←' : '→'} ${step.toLabelKey ? t(step.toLabelKey) : step.toLabel}`,
        percentage:
            step.fromCount > 0
                ? Math.round(
                      ((step.fromCount - step.toCount) / step.fromCount) * 100
                  )
                : 0,
    }));

    const getSubtitle = () => {
        if (isJobView(funnelData)) {
            return t('reportsPage.dropOffRates.jobViewSubtitle');
        }
        return t('reportsPage.dropOffRates.subtitle');
    };

    const getFooter = () => {
        if (isJobView(funnelData)) {
            return t('reportsPage.dropOffRates.jobViewFooter');
        }
        return t('reportsPage.dropOffRates.footer');
    };

    return (
        <StyledConversionCard>
            <Typography variant="h6">
                {t('reportsPage.dropOffRates.title')}
            </Typography>
            <Typography variant="body1" color={theme.palette.grey[500]}>
                {getSubtitle()}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(1.5),
                    marginTop: theme.spacing(2),
                    maxHeight: dropOffItems.length > 4 ? 200 : 'auto',
                    overflowY: dropOffItems.length > 4 ? 'auto' : 'visible',
                }}
            >
                {dropOffItems.map((item, index) => (
                    <Box
                        key={`${item.label}-${index}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Typography
                            variant="body2"
                            color={theme.palette.text.secondary}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '70%',
                            }}
                        >
                            {item.label}{' '}
                            {t('reportsPage.dropOffRates.dropOff', {
                                percentage: item.percentage,
                            })}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing(0.5),
                                color: theme.palette.error.dark,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.error.dark,
                                    fontWeight: 600,
                                }}
                            >
                                {item.percentage}%
                            </Typography>
                            <Icon
                                name="TrendingDownIcon"
                                sx={{
                                    fontSize: 16,
                                    color: theme.palette.error.dark,
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
            <Typography
                variant="caption"
                color={theme.palette.grey[500]}
                sx={{ marginTop: theme.spacing(2) }}
            >
                {getFooter()}
            </Typography>
        </StyledConversionCard>
    );
};

export default DropOffRates;
